import { useEffect } from "react";
import { useTonWallet } from "@tonconnect/ui-react";
import {
  useTonProofOnConnect,
  useWalletsQuery,
  useCreateWalletMutation,
} from "../../src/features/wallets";
import { authService } from "../../src/entities/user";
import { decodeJwt } from "../../src/shared/utils/jwt";
import { TelegramWebAppUtils } from "../../src/shared/api/telegram";
import { walletMatches } from "../../src/features/wallets/lib/address-normalizers";
import { logger } from "../../src/shared/utils/logger";

/**
 * Manages wallet lifecycle - handles TON proof verification
 * and wallet registration when user connects their wallet.
 * 
 * Using refactored implementation with improved architecture and separation of concerns.
 */
export function WalletLifecycleManager() {
  const tonWallet = useTonWallet();
  const walletsQuery = useWalletsQuery();
  const createWalletMutation = useCreateWalletMutation();

  // Check if tokens match current (telegramId, walletId) pair
  useEffect(() => {
    if (!tonWallet?.account?.address || !walletsQuery.data) {
      return;
    }

    const checkTokensMatchWallet = async () => {
      try {
        const tokens = authService.getTokens();
        if (!tokens) {
          return;
        }

        const expectedTelegramId = TelegramWebAppUtils.getInitDataUnsafe()?.user?.id;
        if (!expectedTelegramId) {
          return;
        }

        const payload = decodeJwt(tokens.accessToken);
        const tokenTelegramId = payload?.telegramId ?? payload?.telegram_id;
        const tokenUserId = payload?.sub;
        
        // Check if telegramId matches
        if (tokenTelegramId && Number(tokenTelegramId) !== Number(expectedTelegramId)) {
          logger.warn(
            `[WalletLifecycleManager] Telegram user changed: token has ${tokenTelegramId}, expected ${expectedTelegramId}. Clearing tokens.`,
          );
          await authService.clearTokens();
          
          // Also clear telecom tokens
          try {
            const { telecomTokenService } = await import('../../src/entities/user');
            await telecomTokenService.clearToken();
            
            const { telecomTokenByPhoneService } = await import('../../src/entities/user/telecom-token-by-phone-service');
            telecomTokenByPhoneService.clearAllTokens();
          } catch (error) {
            logger.warn("[WalletLifecycleManager] Failed to clear telecom tokens", error);
          }
          
          // Force re-authentication
          window.location.reload();
          return;
        }

        // Check if wallet belongs to current user
        const matchingWallet = walletsQuery.data.find(w => 
          walletMatches(w, tonWallet.account.address)
        );
        
        if (matchingWallet && tokenUserId) {
          // Wallet found - verify that tokens are valid for this (telegramId, walletId) pair
          // The backend will validate this when we make requests with walletId
          // If backend returns 401/403, it means walletId doesn't belong to current user
          // In that case, tokens will be cleared by http interceptor
          logger.debug(
            `[WalletLifecycleManager] Wallet ${matchingWallet.id} connected, validating tokens for pair (telegramId: ${expectedTelegramId}, walletId: ${matchingWallet.id}, userId: ${tokenUserId})`,
          );
          
          // Note: Actual validation happens on backend when making requests with walletId
          // If wallet doesn't belong to user, backend will return 401/403
          // and tokens will be cleared by http interceptor
        }
      } catch (error) {
        logger.warn("[WalletLifecycleManager] Failed to check tokens match wallet", error);
      }
    };

    checkTokensMatchWallet();
  }, [tonWallet?.account?.address, walletsQuery.data, walletsQuery.dataUpdatedAt]);

  useTonProofOnConnect({
    tonWallet,
    wallets: walletsQuery.data,
    isLoading: walletsQuery.isLoading,
    isError: walletsQuery.isError,
    createWalletMutation,
  });

  return null;
}

